import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { HERO, NAV } from "@/constants/testIds";
import ProfileCard from "@/components/ProfileCard";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronRight, Sparkles, ShieldCheck, HeartHandshake, Search as SearchIcon } from "lucide-react";
import OrthodoxTvSection from "@/components/OrthodoxTvSection";

const HERO_IMG = "https://images.pexels.com/photos/11813966/pexels-photo-11813966.jpeg";

const TESTIMONIALS = [
    { quote: "We met on HolyMarry during Great Lent. By Pascha, we knew. Crowned the next year.", name: "Sophia & Andreas", place: "Athens, Greece" },
    { quote: "A space that honors our faith. We're grateful to have found each other here.", name: "Mary & John", place: "New York, USA" },
    { quote: "Verified profiles and a community that takes marriage seriously. That mattered to us.", name: "Anna & Demetri", place: "Sydney, Australia" },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [form, setForm] = useState({ ageMin: "", ageMax: "", gender: "", denomination: "" });
    const navigate = useNavigate();

    useEffect(() => {
        client.get("/profiles/featured?limit=6").then(({ data }) => setFeatured(data)).catch(() => {});
    }, []);

    const onSearch = () => {
        const params = new URLSearchParams();
        if (form.ageMin) params.set("age_min", form.ageMin);
        if (form.ageMax) params.set("age_max", form.ageMax);
        if (form.gender) params.set("gender", form.gender);
        if (form.denomination) params.set("denomination", form.denomination);
        navigate(`/search?${params.toString()}`);
    };

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-12 md:pt-20 pb-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 bg-linen border border-navy-100 rounded-lg px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-navy-600 font-semibold">
                            <Sparkles className="h-3.5 w-3.5 text-gold-600" />
                            <span>Orthodox Christian Matrimony</span>
                        </div>
                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl text-navy leading-[0.95] tracking-tighter">
                            Find your<br />
                            <span className="italic text-gold-600">perfect</span> life partner
                        </h1>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-navy-500 leading-relaxed font-sans">
                            A reverent, modern sanctuary where faithful Orthodox Christians meet their God-chosen
                            companion. Verified profiles, sacred intentions, lifelong vows.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/register"
                                data-testid={HERO.registerCta}
                                className="font-sans text-xs uppercase tracking-[0.18em] bg-navy text-white rounded-lg px-8 py-4 hover:bg-navy-700 active:scale-95 transition-all shadow-lg shadow-navy/20"
                            >
                                Register Free
                            </Link>
                            <Link
                                to="/search"
                                data-testid={HERO.browseCta}
                                className="font-sans text-xs uppercase tracking-[0.18em] border border-navy-300 text-navy rounded-lg px-8 py-4 hover:border-navy hover:bg-navy hover:text-white active:scale-95 transition-all flex items-center gap-2"
                            >
                                Browse Profiles <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>

                        {/* Floating search bar */}
                        <div className="mt-10 glass rounded-2xl md:rounded-full p-3 md:p-2 shadow-[0_12px_40px_rgba(15,23,42,0.08)] flex flex-col md:flex-row md:items-center gap-2">
                            <div className="flex gap-2 flex-1">
                                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                                    <SelectTrigger className="rounded-full border-0 bg-transparent focus:ring-0 h-12 px-4" data-testid={HERO.gender}>
                                        <SelectValue placeholder="Looking for" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">A Groom</SelectItem>
                                        <SelectItem value="female">A Bride</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={form.ageMin} onValueChange={(v) => setForm({ ...form, ageMin: v })}>
                                    <SelectTrigger className="rounded-full border-0 bg-transparent focus:ring-0 h-12 px-4" data-testid={HERO.ageMin}>
                                        <SelectValue placeholder="Age from" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[21,23,25,27,29,31,33,35].map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-2 flex-1">
                                <Select value={form.ageMax} onValueChange={(v) => setForm({ ...form, ageMax: v })}>
                                    <SelectTrigger className="rounded-full border-0 bg-transparent focus:ring-0 h-12 px-4" data-testid={HERO.ageMax}>
                                        <SelectValue placeholder="Age to" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[25,28,30,33,35,40,45,50].map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Select value={form.denomination} onValueChange={(v) => setForm({ ...form, denomination: v })}>
                                    <SelectTrigger className="rounded-full border-0 bg-transparent focus:ring-0 h-12 px-4" data-testid={HERO.denomination}>
                                        <SelectValue placeholder="Denomination" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Greek Orthodox">Greek Orthodox</SelectItem>
                                        <SelectItem value="Russian Orthodox">Russian Orthodox</SelectItem>
                                        <SelectItem value="Antiochian Orthodox">Antiochian Orthodox</SelectItem>
                                        <SelectItem value="Romanian Orthodox">Romanian Orthodox</SelectItem>
                                        <SelectItem value="Bulgarian Orthodox">Bulgarian Orthodox</SelectItem>
                                        <SelectItem value="Malankara Orthodox">Malankara Orthodox</SelectItem>
                                        <SelectItem value="Coptic Orthodox">Coptic Orthodox</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <button
                                onClick={onSearch}
                                data-testid={HERO.searchButton}
                                className="h-12 px-6 md:px-8 bg-gold text-navy rounded-full font-sans text-xs uppercase tracking-[0.15em] font-medium hover:bg-gold-600 hover:text-white active:scale-95 transition-all shadow-md shadow-gold/30 flex items-center justify-center gap-2"
                            >
                                <SearchIcon className="h-4 w-4" /> Search
                            </button>
                        </div>

                        <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-navy-400 font-sans uppercase tracking-[0.18em]">
                            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold-600" /> Verified Profiles</div>
                            <div className="flex items-center gap-2"><HeartHandshake className="h-4 w-4 text-gold-600" /> Sacred Intentions</div>
                            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold-600" /> 100% Confidential</div>
                        </div>
                    </div>

                    {/* Arched image */}
                    <div className="lg:col-span-5 relative animate-fade-up">
                        <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-transparent to-navy/10 rounded-[240px_240px_24px_24px] blur-2xl"></div>
                        <div className="relative aspect-[4/5] overflow-hidden arch-mask-lg shadow-2xl">
                            <img src={HERO_IMG} alt="Orthodox couple" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-5 py-4 border border-navy-100 hidden md:block">
                            <div className="text-xs uppercase tracking-[0.18em] text-navy-400 font-sans">Trusted by</div>
                            <div className="font-serif text-3xl text-navy">12,000+</div>
                            <div className="text-xs text-navy-500 font-sans">faithful members</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED PROFILES */}
            <section id="featured" className="py-24 md:py-32 linen-bg">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Featured Today</div>
                            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight">Profiles you&rsquo;ll cherish</h2>
                        </div>
                        <Link to="/search" className="font-sans text-xs uppercase tracking-[0.18em] text-navy hover:text-gold-600 flex items-center gap-1">
                            View all <ChevronRight className="h-4 w-4" />
                        </Link>
                    </div>
                    {featured.length === 0 ? (
                        <div className="text-center py-12 text-navy-400 font-sans">Loading profiles…</div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {featured.map((p) => <ProfileCard key={p.user_id} profile={p} />)}
                        </div>
                    )}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="py-24 md:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">A Sacred Journey</div>
                            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight">How it works</h2>
                            <p className="mt-4 text-navy-500 leading-relaxed font-sans max-w-md">
                                Four prayerful steps, designed to honor the seriousness of marriage and the sanctity of the union.
                            </p>
                        </div>
                        <div className="space-y-6">
                            {[
                                { n: "01", t: "Create your profile", d: "Share who you are with reverence — faith, family, aspirations." },
                                { n: "02", t: "Discover faithful matches", d: "Browse verified Orthodox Christians worldwide." },
                                { n: "03", t: "Express your interest", d: "Send a meaningful note. No swipes, only sacred intentions." },
                                { n: "04", t: "Build the covenant", d: "Message, meet, and walk together toward holy matrimony." },
                            ].map((s) => (
                                <div key={s.n} className="flex gap-5 group">
                                    <div className="font-serif text-3xl text-gold-600 italic w-12 shrink-0">{s.n}</div>
                                    <div>
                                        <h3 className="font-serif text-2xl text-navy">{s.t}</h3>
                                        <p className="mt-1 text-sm text-navy-500 font-sans leading-relaxed">{s.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section id="stories" className="py-24 md:py-32 linen-bg">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Success Stories</div>
                        <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight">Crowned in love</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="bg-white rounded-2xl p-8 border border-navy-100 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
                                <div className="font-serif text-5xl text-gold-500 leading-none">&ldquo;</div>
                                <p className="mt-4 font-serif text-2xl text-navy leading-snug italic">{t.quote}</p>
                                <div className="mt-6 pt-6 border-t border-navy-100">
                                    <div className="font-sans text-sm font-semibold text-navy">{t.name}</div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-navy-400">{t.place}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <OrthodoxTvSection />

            <section className="py-24 md:py-32 bg-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-700"></div>
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
                <div className="relative max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-tight">
                        Your sacred vows<br /><span className="italic text-gold">begin here</span>
                    </h2>
                    <p className="mt-6 text-navy-200 max-w-xl mx-auto font-sans">
                        Join thousands of faithful Orthodox Christians on their journey toward holy matrimony.
                    </p>
                    <Link
                        to="/register"
                        className="inline-block mt-10 font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy rounded-lg px-10 py-4 hover:bg-white active:scale-95 transition-all shadow-2xl shadow-gold/30"
                    >
                        Begin Your Journey
                    </Link>
                </div>
            </section>
        </div>
    );
}
